import React, {useEffect, useRef} from 'react';
import {observer} from 'mobx-react';
import classNames from 'classnames';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {TsMultiSelectController} from './ts-multi-select.controller';

import './ts-multi-select.style.scss';

interface TsMultiSelectProps {
    controller: TsMultiSelectController;
}

@observer
export class TsMultiSelect extends TechseeBaseComponent<TsMultiSelectProps> {
    private componentRef = React.createRef<HTMLDivElement>();

    componentDidMount(): void {
        this.props.controller.updateButtonText;
    }

    componentDidUpdate(prevProps: TsMultiSelectProps): void {
        // Re-update if controller changes
        if (prevProps.controller !== this.props.controller) {
            this.props.controller.updateButtonText;
        }
    }

    componentWillUnmount(): void {
        this.removeClickOutsideHandler();
    }

    private setupClickOutsideHandler(): void {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    private removeClickOutsideHandler(): void {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    private handleClickOutside = (event: MouseEvent): void => {
        if (this.componentRef.current && !this.componentRef.current.contains(event.target as Node)) {
            this.props.controller.close();
        }
    };

    private handleToggleOpen = (event: React.MouseEvent): void => {
        event.preventDefault();
        event.stopPropagation();
        this.props.controller.toggleOpen();

        // Setup click outside handler when dropdown opens
        if (this.props.controller.isOpen) {
            this.setupClickOutsideHandler();
        }
    };

    private handleSelectItem = (item: any, event: React.MouseEvent): void => {
        event.preventDefault();
        event.stopPropagation();

        this.props.controller.selectItem(item);

        // Close dropdown after selection for single mode
        if (this.props.controller.getSelectionMode() === 'single') {
            this.props.controller.close();
        }
    };

    renderInternal(): JSX.Element {
        const {controller} = this.props;

        // Ensure controller is available
        if (!controller) {
            return <div>Error: Controller not available</div>;
        }

        // Get data from controller
        const currentInputModel = controller.getInputModel();
        const currentInputId = controller.getInputId();
        const controlName = controller.getControlName();
        const showTooltipOffset = 30;
        const required = false;

        const buttonClasses = classNames('tsms-btn', {
            'login-page': currentInputId === 'login',
            'bright-multi-select': controller.isBrightMultiSelect()
        });

        const containerClasses = classNames('ts-multi-select', {
            'big-label': currentInputId === 'big-label',
            'tsms-options-open': controller.isOpen
        });

        const optionsClasses = classNames('tsms-options', {
            'login-options': currentInputId === 'login',
            'bright-tsms-options': controller.isBrightMultiSelect(),
            'language-options': currentInputId === 'language'
        });

        const buttonText = controller.buttonText;
        const hiddenValue = controller.getHiddenValue();
        const itemLabelArr = controller.getItemLabelArr();
        const selectedProperty = controller.getSelectedProperty();

        return (
            <div ref={this.componentRef} className={containerClasses}>
                <input
                    type='hidden'
                    name={controlName}
                    className='form-control'
                    value={hiddenValue}
                    required={required}
                />

                <div className={containerClasses} onClick={this.handleToggleOpen}>
                    <div className={buttonClasses}>
                        <div className='tsms-flex-wrap'>
                            <span
                                className={classNames('tsms-btn-text', {
                                    disabled: currentInputModel.length === 0
                                })}
                                title={buttonText.length > showTooltipOffset ? buttonText : ''}>
                                {buttonText.length > showTooltipOffset
                                    ? `${buttonText.substring(0, showTooltipOffset)}...`
                                    : buttonText}
                            </span>
                            <span
                                className={classNames('tsms-caret', {
                                    'bright-tsms-caret': controller.isBrightMultiSelect()
                                })}
                            />
                        </div>
                    </div>

                    {controller.isOpen && (
                        <ul className={optionsClasses}>
                            {currentInputModel.map((item: any, index: number) => (
                                <li key={index}>
                                    <a
                                        href='#'
                                        className={classNames({
                                            'tsms-selected': item[selectedProperty]
                                        })}
                                        onClick={(e) => this.handleSelectItem(item, e)}>
                                        {itemLabelArr.map((label: string, labelIndex: number) => (
                                            <span key={labelIndex}>
                                                {item[label] && item[label].length > 30
                                                    ? `${item[label].substring(0, 30)}...`
                                                    : item[label]}
                                            </span>
                                        ))}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        );
    }
}

export default TsMultiSelect;
